import { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { MDSPanelBody, MDSPanelHeader } from '@marqvision/mds/core';
import { useRequestCountQuery, useRequestListQuery } from '~/apis/customerRequest';
import { ClientRequestProtocolGetCrListResponseCR } from '~/swagger/data-contracts';
import useOnScreen from '~/hooks/useOnScreen';
import { requestFilterAtom } from '../@atoms';
import { RequestFilter } from '../@types';
import { FilterSection } from './@components/FilterSection';
import { ListTable } from './ListTable';
import { ActionSection } from './@components/ActionSection';

const useStyles = makeStyles({
  body: {
    transition: '200ms width',
    display: 'grid',
    gridTemplateRows: 'auto auto 1fr',
  },
  viewMoreWrapper: {
    height: '1px',
    '&.hidden': {
      display: 'none',
    },
  },
});

type Props = {
  onAddMode: () => void;
  onViewDetail: (target: number) => void;
  onClose: () => void;
};

export const RequestList = (props: Props) => {
  const { onAddMode, onViewDetail, onClose } = props;
  const classes = useStyles();

  const moreRef = useRef<HTMLDivElement>(null);
  const isOnScreen = useOnScreen(moreRef);
  
  const [filter, setFilter] = useAtom(requestFilterAtom);
  
  const {
    data: listData,
    isLoading: isListLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRequestListQuery({ ...filter, limit: 30, offset: 0 });
  const { data: countData, isLoading: isCountLoading } = useRequestCountQuery(filter);

  const requestList = listData?.pages.reduce<ClientRequestProtocolGetCrListResponseCR[]>((prev, cur) => ([...prev, ...cur.crList]), []) || [];

  const handleFilter = (key: keyof RequestFilter, value: RequestFilter[keyof RequestFilter]) => {
    setFilter((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  useEffect(() => {
    if (!hasNextPage || !isOnScreen) return;
    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isListLoading, isOnScreen]);

  return (
    <>
      <MDSPanelHeader title="Requests" onClose={onClose} />
      <MDSPanelBody className={classes.body}>
        <FilterSection onAddMode={onAddMode} filter={filter} onFilter={handleFilter} />
        <ActionSection isLoading={isCountLoading} totalCount={countData?.crCount} />
        <ListTable
          isLoading={isListLoading}
          data={requestList}
          onViewDetail={onViewDetail}
          viewMoreBox={
            <div
              className={clsx(classes.viewMoreWrapper, { hidden: (isListLoading || isFetchingNextPage) })}
              ref={moreRef}
            />
          }
        />
      </MDSPanelBody>
    </>
  );
};